/**
 * Service API Abstraction Layer
 * 
 * Auto-syncs mock data with real client campaign metrics from Looker Studio reports.
 */

import { initialLeads } from '../data/demoLeads';
import { initialActivities } from '../data/demoActivities';

const STORAGE_KEYS = {
  LEADS: "tbs_crm_leads_v4",
  ACTIVITIES: "tbs_crm_activities_v4",
  SETTINGS: "tbs_crm_settings_v4"
};

export const apiService = {
  // Leads API
  getLeads: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.LEADS);
      if (!data) {
        localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(initialLeads));
        return initialLeads;
      }
      return JSON.parse(data);
    } catch {
      return initialLeads;
    }
  },

  saveLeads: (leads) => {
    localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
  },

  getLeadById: (id) => {
    const leads = apiService.getLeads();
    return leads.find(l => l.id === id) || null;
  },

  createLead: (leadData) => {
    const leads = apiService.getLeads();
    const newId = `RPM-${1000 + leads.length + 1}`;
    const newLead = {
      id: newId,
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      score: 85,
      status: "qualified",
      assignedTo: "TBS Sales Team",
      notes: [],
      conversation: [],
      ...leadData
    };
    const updated = [newLead, ...leads];
    apiService.saveLeads(updated);
    return newLead;
  },

  updateLead: (leadId, updatedFields) => {
    const leads = apiService.getLeads();
    const updated = leads.map(l => {
      if (l.id === leadId) {
        return { ...l, ...updatedFields, lastActivity: new Date().toISOString() };
      }
      return l;
    });
    apiService.saveLeads(updated);
    return updated;
  },

  updateLeadStatus: (leadId, newStatus) => {
    const leads = apiService.getLeads();
    const updated = leads.map(l => {
      if (l.id === leadId) {
        return { ...l, status: newStatus, lastActivity: new Date().toISOString() };
      }
      return l;
    });
    apiService.saveLeads(updated);
    return updated;
  },

  updateLeadAssignee: (leadId, newAssignee) => {
    const leads = apiService.getLeads();
    const updated = leads.map(l => {
      if (l.id === leadId) {
        return { ...l, assignedTo: newAssignee, lastActivity: new Date().toISOString() };
      }
      return l;
    });
    apiService.saveLeads(updated);
    return updated;
  },

  deleteLead: (leadId) => {
    const leads = apiService.getLeads();
    const updated = leads.filter(l => l.id !== leadId);
    apiService.saveLeads(updated);
    return updated;
  },

  addLeadNote: (leadId, noteText, author = "TBS Sales Team") => {
    const leads = apiService.getLeads();
    const updated = leads.map(l => {
      if (l.id === leadId) {
        const notes = l.notes || [];
        const newNote = { id: Date.now(), text: noteText, author, date: new Date().toISOString() };
        return { ...l, notes: [newNote, ...notes], lastActivity: new Date().toISOString() };
      }
      return l;
    });
    apiService.saveLeads(updated);
    return updated;
  },

  // Activities / Live Feed API
  getActivities: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.ACTIVITIES);
      return data ? JSON.parse(data) : initialActivities;
    } catch {
      return initialActivities;
    }
  },

  addActivity: (activity) => {
    const current = apiService.getActivities();
    const newAct = { id: Date.now(), time: "Just now", ...activity };
    const updated = [newAct, ...current.slice(0, 14)];
    localStorage.setItem(STORAGE_KEYS.ACTIVITIES, JSON.stringify(updated));
    return updated;
  },

  // Reset Demo Data
  resetDemoData: () => {
    localStorage.removeItem(STORAGE_KEYS.LEADS);
    localStorage.removeItem("tbs_crm_leads");
    localStorage.removeItem("tbs_crm_leads_v2");
    localStorage.removeItem("tbs_crm_leads_v3");
    localStorage.removeItem(STORAGE_KEYS.ACTIVITIES);
    localStorage.removeItem(STORAGE_KEYS.SETTINGS);
    localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(initialLeads));
    return true;
  }
};
